'use client'

import Button from 'src/components/design-system/Button'
import Typography from 'src/components/design-system/Typography'
import { ReviewerItem } from 'src/components/items/ReviewerItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'

// icon
import Arrow from 'public/icons/arrows/arrow-right.svg'
// end icon
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'

const buildColumnBorders = (index) => {
  const classes = []

  if (index % 2 === 1) {
    classes.push('t:border-l', 't:border-basic-gray8', 't:pl-20')
  }

  if (index % 4 !== 0) {
    classes.push('d:border-l', 'd:border-basic-gray8', 'd:pl-20')
  }

  return classes.join(' ')
}
export default function ReviewersClient({ block, locale, monograph = null, variant = '' }) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const isDomainJournal = variant === 'journal-domain'
  const items = Array.isArray(block.items) ? block.items : []
  const blockTranslation = getPreferredTranslation(block, locale) || {}
  const fallbackTitle = variant === 'monograph' ? translate('T_MONOGRAPH_BLOCK_REVIEWERS_DEFAULT_TITLE') : ''
  const blockTitle = blockTranslation?.title?.trim() || fallbackTitle
  const journalSlug = getPreferredTranslation(block.journal, locale)?.slug
  const monographSlug = getPreferredTranslation(monograph, locale)?.slug
  const linkReviewers =
    variant === 'monograph'
      ? buildLocalizedPath(LINKS.MONOGRAPHS.REVIEWERS, locale, { slug: monographSlug })
      : buildLocalizedPath(
          isDomainJournal ? journalRoutes.JOURNALS.OUR_REVIEWERS : LINKS.JOURNALS.OUR_REVIEWERS,
          locale,
          { journal: journalSlug },
        )

  if (!items.length) return null

  return (
    <section
      className={`${variant === 'article' ? 'my-64 d:my-80' : 'my-64 d:px-40 px-16'} ${block.type.value}`}
      id={block.uuid}
    >
      {blockTitle && (
        <Typography variant="h2-caps-bold" color="text-basic-black" className="pb-32">
          {blockTitle}
        </Typography>
      )}

      <div className="grid gap-24 t:grid-cols-2 d:grid-cols-4">
        {items.slice(0, 4).map((item, i) => {
          const columnBorders = buildColumnBorders(i)
          const translation = getPreferredTranslation(item, locale) || {}
          const slug = item.slug
          const position = translation.academicPrefix
          const fullName = item.fullName
          const affiliation = item.affiliations
            ?.map((aff) => getPreferredTranslation(aff, locale)?.title)
            .filter((title) => title && title.trim())
            .join(', ')
          const url =
            buildLocalizedPath(
              variant === 'journal-domain' ? journalRoutes.JOURNALS.REVIEWER_DETAIL : LINKS.REVIEWERS.DETAIL,
              locale,
              { slug },
            ) || ' '
          return (
            <ReviewerItem
              key={i}
              // title={title}
              fullname={fullName}
              affiliation={affiliation}
              url={url}
              position={position}
              className={`pb-16 ${columnBorders}`}
            />
          )
        })}
      </div>
      {variant === 'article' ? null : linkReviewers && (journalSlug || monographSlug) && items.length > 4 ? (
        <Button
          href={linkReviewers}
          className={`w-fit mt-24 d:mt-56 relative mx-auto block ${
            isDomainJournal
              ? '!border-basic-black !bg-transparent !text-basic-black hover:!bg-basic-gray8'
              : 'bg-transparent hover:bg-basic-white'
          }`}
          size="l"
        >
          <span className="flex items-center gap-x-8">
            {translate('T_SEE_REVIEWERS')}
            <Arrow className={isDomainJournal ? 'text-basic-black' : 'text-dark-blue-dark-blue2'} />
          </span>
        </Button>
      ) : null}
    </section>
  )
}
